/**
 * Owner: Masa Bayu
 * File: commerce-entitlement-issue.ts
 * Date:Time: 2026-09-15 22:46
 * Description: T3-E2 entitlement issuance boundary after confirmed payment (≠ Library/Reader).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { paymentAllowsEntitlement } from './commerce-payment';
import { assertT3E2SecurityIntact } from './commerce-security';
import { createQxreadEntitlement } from './entitlement-boundary';
import { createQxreadId } from './id';
import { assertValidId } from './guards';
/**
 * Issue owned entitlements for each confirmed order line.
 * Does not open Library UI, launch Reader, or mutate QXP.
 */
export function issueEntitlementsForConfirmedOrder(order, attempt, options) {
    assertNoForbiddenFoundationKey(options, 'QXRead entitlement issuance');
    assertT3E2SecurityIntact();
    assertValidId(options.accountId, 'QXRead entitlement accountId');
    if (attempt.orderId !== order.orderId || attempt.attemptId !== order.paymentAttemptId) {
        throw new Error('QXRead entitlement issuance payment/order mismatch');
    }
    if (!paymentAllowsEntitlement(attempt)) {
        throw new Error('QXRead entitlement issuance requires succeeded payment');
    }
    const existing = options.existingBatchByOrderId?.get(order.orderId);
    if (existing !== undefined) {
        return Object.freeze({
            batch: existing,
            order: Object.freeze({
                ...order,
                entitlementBatchId: existing.batchId,
            }),
            duplicate: true,
        });
    }
    const batchId = options.batchId !== undefined ? options.batchId : createQxreadId('qxrdentb');
    assertValidId(batchId, 'QXRead entitlement batchId');
    const issuedAt = options.issuedAt ?? new Date().toISOString();
    const entitlements = Object.freeze(order.lines.map((line) => createQxreadEntitlement({
        accountId: options.accountId,
        publicationId: line.publicationId,
        scope: 'owned',
        state: 'active',
        createdAt: issuedAt,
    })));
    const batch = Object.freeze({
        batchId,
        orderId: order.orderId,
        paymentAttemptId: order.paymentAttemptId,
        accountId: options.accountId,
        entitlements,
        issuedAt,
    });
    return Object.freeze({
        batch,
        order: Object.freeze({
            ...order,
            entitlementBatchId: batchId,
        }),
        duplicate: false,
    });
}
/** Entitlement boundary ≠ Library access and ≠ Reader launch. */
export function entitlementIssuanceDoesNotOpenLibrary(batch) {
    return batch.entitlements.every((item) => item.scope === 'owned' && (item.state === 'active' || item.state === 'pending_grant'));
}
//# sourceMappingURL=commerce-entitlement-issue.js.map